<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useResponsiveness } from 'dashboard/composables/zimobi/useResponsiveness';
import {
  PINNED_CONVERSATION_SIDEBAR_ITEM,
  canMoveConversationSidebarItem,
  useConversationSidebarSettings,
} from 'dashboard/composables/useUISettings';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

// Zimobi: keep mobile and desktop UI settings isolated when the viewport
// crosses the responsive breakpoint while this panel is mounted.
const { isAtMobile } = useResponsiveness();
const { uiSettings, updateUISettings, conversationSidebarItemsOrder } =
  useConversationSidebarSettings(isAtMobile);

// Zimobi: keep the conversation unread when it is transferred from this
// panel to another agent or team.
const onAgentOrTeamChange = () => {
  emitter.emit(BUS_EVENTS.AGENT_OR_TEAM_CHANGED);
};

const conversationSidebarItems = ref([]);

// Zimobi: actions stay pinned and expanded. Other sections can be collapsed
// and reordered on desktop, while mobile remains fixed and expanded.
const isSectionInteractive = name =>
  !isAtMobile.value && name !== PINNED_CONVERSATION_SIDEBAR_ITEM;

const isSectionOpen = key => uiSettings.value[key] ?? true;

const toggleSection = key => updateUISettings({ [key]: !isSectionOpen(key) });

const onDragEnd = () => {
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

// Zimobi: reload the correct saved order after a responsive source change.
watch(
  conversationSidebarItemsOrder,
  itemsOrder => {
    conversationSidebarItems.value = [...itemsOrder];
  },
  { immediate: true }
);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

onMounted(() => {
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Zimobi: load Linear state before rendering its optional sidebar section.
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <!-- Zimobi: descendant styling uses Tailwind utilities, not scoped CSS. -->
  <div
    class="w-full [&_.contact--profile]:pb-3 [&_.contact--profile]:border-b [&_.contact--profile]:border-solid [&_.contact--profile]:border-n-weak"
  >
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      @close="closeContactPanel"
    />
    <ContactInfo :contact="contact" :channel-type="channelType" />
    <div class="px-2 pb-8 list-group">
      <Draggable
        :list="conversationSidebarItems"
        :disabled="isAtMobile"
        :move="canMoveConversationSidebarItem"
        animation="200"
        ghost-class="ghost"
        handle=".cursor-grab"
        item-key="name"
        class="flex flex-col gap-3"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div :data-sidebar-item="element.name">
            <div
              v-if="element.name === 'conversation_actions'"
              class="conversation--actions"
            >
              <AccordionItem
                :title="
                  $t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')
                "
                :collapsible="false"
                :draggable="false"
              >
                <ConversationAction
                  :conversation-id="conversationId"
                  :inbox-id="inboxId"
                  @agent-or-team-change="onAgentOrTeamChange"
                />
              </AccordionItem>
            </div>
            <div
              v-else-if="element.name === 'conversation_participants'"
              class="conversation--actions"
            >
              <AccordionItem
                :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
                :collapsible="isSectionInteractive(element.name)"
                :draggable="isSectionInteractive(element.name)"
                :is-open="isSectionOpen('is_conv_participants_open')"
                @toggle="toggleSection('is_conv_participants_open')"
              >
                <ConversationParticipant
                  :conversation-id="conversationId"
                  :inbox-id="inboxId"
                />
              </AccordionItem>
            </div>
            <div v-else-if="element.name === 'conversation_info'">
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
                compact
                :collapsible="isSectionInteractive(element.name)"
                :draggable="isSectionInteractive(element.name)"
                :is-open="isSectionOpen('is_conv_details_open')"
                @toggle="toggleSection('is_conv_details_open')"
              >
                <ConversationInfo
                  :conversation-attributes="conversationAdditionalAttributes"
                  :contact-attributes="contactAdditionalAttributes"
                />
              </AccordionItem>
            </div>
            <div v-else-if="element.name === 'contact_attributes'">
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
                compact
                :collapsible="isSectionInteractive(element.name)"
                :draggable="isSectionInteractive(element.name)"
                :is-open="isSectionOpen('is_contact_attributes_open')"
                @toggle="toggleSection('is_contact_attributes_open')"
              >
                <CustomAttributes
                  attribute-type="contact_attribute"
                  attribute-from="conversation_contact_panel"
                  :contact-id="contact.id"
                  :empty-state-message="
                    $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                  "
                />
              </AccordionItem>
            </div>
            <div v-else-if="element.name === 'previous_conversation'">
              <AccordionItem
                v-if="contact.id"
                :title="
                  $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
                "
                compact
                :collapsible="isSectionInteractive(element.name)"
                :draggable="isSectionInteractive(element.name)"
                :is-open="isSectionOpen('is_previous_conv_open')"
                @toggle="toggleSection('is_previous_conv_open')"
              >
                <ContactConversations
                  :contact-id="contact.id"
                  :conversation-id="conversationId"
                />
              </AccordionItem>
            </div>
            <woot-feature-toggle
              v-else-if="element.name === 'macros'"
              feature-key="macros"
            >
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
                compact
                :collapsible="isSectionInteractive(element.name)"
                :draggable="isSectionInteractive(element.name)"
                :is-open="isSectionOpen('is_macro_open')"
                @toggle="toggleSection('is_macro_open')"
              >
                <MacrosList :conversation-id="conversationId" />
              </AccordionItem>
            </woot-feature-toggle>
            <div
              v-else-if="
                element.name === 'linear_issues' &&
                isLinearFeatureEnabled &&
                isLinearClientIdConfigured
              "
            >
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
                compact
                :collapsible="isSectionInteractive(element.name)"
                :draggable="isSectionInteractive(element.name)"
                :is-open="isSectionOpen('is_linear_issues_open')"
                @toggle="toggleSection('is_linear_issues_open')"
              >
                <LinearSetupCTA v-if="!isLinearConnected" />
                <LinearIssuesList v-else :conversation-id="conversationId" />
              </AccordionItem>
            </div>
            <div
              v-else-if="
                element.name === 'shopify_orders' && isShopifyFeatureEnabled
              "
            >
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
                compact
                :collapsible="isSectionInteractive(element.name)"
                :draggable="isSectionInteractive(element.name)"
                :is-open="isSectionOpen('is_shopify_orders_open')"
                @toggle="toggleSection('is_shopify_orders_open')"
              >
                <ShopifyOrdersList :contact-id="contactId" />
              </AccordionItem>
            </div>
            <div v-else-if="element.name === 'contact_notes'">
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
                compact
                :collapsible="isSectionInteractive(element.name)"
                :draggable="isSectionInteractive(element.name)"
                :is-open="isSectionOpen('is_contact_notes_open')"
                @toggle="toggleSection('is_contact_notes_open')"
              >
                <ContactNotes :contact-id="String(contactId)" />
              </AccordionItem>
            </div>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>
